/**
 * Upload Progress Modal Component
 *
 * Displays real-time three-stage progress for custom archive folder uploads:
 * - Stage 0 (0-100%): Archive preparation - reading files, building manifest (orange progress bar)
 * - Stage 1 (0-100%): Archive chunk upload to server (blue progress bar)
 * - Stage 2 (0-100%): Backend file extraction and processing (green progress bar)
 * - Complete: Shows final counts and auto-hides after 3 seconds
 */

import React from 'react';
import { useUpload } from '../../../contexts/UploadContext';
import { useAdminTranslation } from '../../../modules/i18n/hooks/useTranslation';

interface ProgressItemProps {
  uploadId: string;
  progress: {
    progress: number;
    phase: 'preparing' | 'uploading' | 'processing' | 'complete';
    filesProcessed: number;
    totalFiles: number;
    filesFailed: number;
  };
}

const ProgressItem = React.memo<ProgressItemProps>(({ progress }) => {
  const { t } = useAdminTranslation();
  return (
    <div className='mb-4 last:mb-0'>
      {/* Header */}
      <div className='mb-2 flex items-center justify-between text-sm'>
        <span
          className={`font-medium ${
            progress.phase === 'complete'
              ? 'text-green-700'
              : progress.phase === 'preparing'
                ? 'text-orange-700'
                : progress.phase === 'uploading'
                  ? 'text-blue-700'
                  : 'text-gray-700'
          }`}>
          {t(`content.documents.uploadProgress.phases.${progress.phase}`)}
        </span>
        <span className={`font-semibold ${progress.phase === 'complete' ? 'text-green-700' : 'text-gray-900'}`}>
          {Math.round(progress.progress)}%
        </span>
      </div>

      {/* Progress bar */}
      <div className='mb-2 h-2 w-full overflow-hidden rounded-full bg-gray-200'>
        <div
          className={`h-full transition-[width] duration-200 ease-linear ${
            progress.phase === 'preparing'
              ? 'bg-orange-500'
              : progress.phase === 'uploading'
                ? 'bg-blue-600'
                : progress.phase === 'processing'
                  ? 'bg-green-600'
                  : 'bg-green-600'
          }`}
          style={{ width: `${progress.progress}%`, willChange: 'width' }}
        />
      </div>

      {/* Preparing message */}
      {progress.phase === 'preparing' && (
        <div className='text-xs text-orange-600'>
          {t('content.documents.uploadProgress.messages.readingFiles', {
            current: progress.filesProcessed,
            total: progress.totalFiles
          })}
        </div>
      )}

      {/* Uploading message */}
      {progress.phase === 'uploading' && (
        <div className='text-xs text-blue-600'>
          {t('content.documents.uploadProgress.messages.uploadingFiles', {
            total: progress.totalFiles
          })}
        </div>
      )}

      {/* Processing file counts */}
      {progress.phase === 'processing' && (
        <div className='text-xs text-gray-600'>
          {t('content.documents.uploadProgress.messages.filesProcessed', {
            current: progress.filesProcessed,
            total: progress.totalFiles
          })}
          {progress.filesFailed > 0 && (
            <span className='ml-2 text-red-600'>
              ({t('content.documents.uploadProgress.messages.filesWithErrors', { count: progress.filesFailed })})
            </span>
          )}
        </div>
      )}

      {/* Completion message */}
      {progress.phase === 'complete' && (
        <div className='text-xs text-green-600'>
          {t('content.documents.uploadProgress.messages.filesProcessedSuccess', {
            current: progress.filesProcessed,
            total: progress.totalFiles
          })}
          {progress.filesFailed > 0 && (
            <span className='ml-1 text-red-600'>
              ({t('content.documents.uploadProgress.messages.filesWithErrors', { count: progress.filesFailed })})
            </span>
          )}
        </div>
      )}
    </div>
  );
});

 ProgressItem.displayName = 'ProgressItem';

export const UploadProgressModal = React.memo(() => {
  const { uploadProgress } = useUpload();

  // Show all uploads including complete ones (they auto-cleanup after 3s)
  const uploads = Object.entries(uploadProgress);

  if (uploads.length === 0) return null;

  return (
    <div className='fixed bottom-4 right-4 z-50 w-96 rounded-lg bg-white shadow-lg'>
      <div className='p-4'>
        {uploads.map(([uploadId, progress]) => (
          <ProgressItem key={uploadId} uploadId={uploadId} progress={progress} />
        ))}
      </div>
    </div>
  );
});

 UploadProgressModal.displayName = 'UploadProgressModal';